import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Typography,
  Popconfirm,
  Empty,
  Alert,
  Tag,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined,
  RollbackOutlined,
} from '@ant-design/icons';

import {
  fetchAssets,
  listPresets,
  createPreset,
  REGIONS,
  selectors,
  updatePreset,
  duplicatePreset,
  deleteCorrelationValues,
  deletePreset,
  fetchPresetTrainerAvailability,
  makePresetAssetVisible,
} from 'state';
import { columnSearchWidget } from 'components';
import { useModal } from 'services';
import SeeMissingPricesModal from './seeMissingPricesModal';
import DuplicatePresetModal from './duplicatePresetModal';
import CreateGeographyModal from './CreateGeographyModal';
import CreatePresetModal from './CreatePresetModal';
import EditPresetNameModal from './EditPresetNameModal';
import { presetsWaiting } from '../../../state/app/selectors';
import SeePresetReturnsModal from './seePresetReturnsModal';

const PresetsTab: React.FC = () => {
  const dispatch = useDispatch();
  const presetsWaiting = useSelector(selectors.presetsWaiting);
  // const assets = useSelector(selectors.underlyingAssetsList, () => true);
  const assets = useSelector(selectors.underlyingAssetsList);
  const currencies = useSelector(selectors.currencies);
  const presets = useSelector(selectors.presets);
  const trainerAvailability = useSelector(selectors.presetTrainerAvailability);
  const availabilityWaiting = useSelector(
    selectors.presetTrainerAvailabilityWaiting,
  );
  const assetBeingMadeVisible = useSelector(
    selectors.presetAssetBeingMadeVisible
  );
  const [missingPricesModal, showMissingPricesModal] = useModal(
    SeeMissingPricesModal
  );
  const [presetReturnsModal, showPresetReturnsModal] = useModal(
    SeePresetReturnsModal
  );
  const [duplicatePresetModal, showDuplicatePresetModal] =
    useModal(DuplicatePresetModal);

  const [selected, setSelected] = useState<number[]>([]);
  const [presetId, setPresetId] = useState<number | undefined>(
    // Object.values(presets)[0]?.id || undefined
    undefined
  );
  const [presetName, setPresetName] = useState('');
  const [showBlockingOnly, setShowBlockingOnly] = useState(false);
  const [CreateModal, showCreateModal] = useModal(CreatePresetModal);
  const [EditNameModal, showEditNameModal] = useModal(EditPresetNameModal);
  const skippedInitialAvailabilityRefresh = useRef(false);

  useEffect(() => {
    dispatch(fetchAssets({}));
    dispatch(listPresets({}));
  }, []);

  useEffect(() => {
    if (!skippedInitialAvailabilityRefresh.current) {
      skippedInitialAvailabilityRefresh.current = true;
      if (!Object.keys(presets).length) return;
    }
    dispatch(fetchPresetTrainerAvailability({}));
  }, [dispatch, presets]);

  useEffect(() => {
    if (presetId && !presets[presetId]) {
      const all = Object.values(presets);
      const last = all[all.length - 1];
      if (last) {
        setPresetId(last.id);
        setSelected(last.assets || []);
        setPresetName(last.name || '');
      } else {
        // prevents random numbers showing in Select if user just deleted last preset
        // no presets left
        setPresetId(undefined);
        setSelected([]);
        setPresetName('');
      }
    }

    const all = Object.values(presets);
    const last = all[all.length - 1];
    if (last) {
      setPresetId(last.id);
      setSelected(last.assets || []);
      setPresetName(last.name || '');
    }
    // }
  }, [presets]);

  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          Select a preset or create one
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  const selectedAvailability =
    presetId === undefined ? undefined : trainerAvailability[presetId];
  const blockingAssetIds = useMemo(
    () =>
      new Set(
        (selectedAvailability?.blocking_assets || []).map(asset => asset.id),
      ),
    [selectedAvailability],
  );
  const displayedAssets = showBlockingOnly
    ? assets.filter(asset => blockingAssetIds.has(asset.id))
    : assets;

  useEffect(() => {
    if (!selectedAvailability?.blocking_asset_count) {
      setShowBlockingOnly(false);
    }
  }, [selectedAvailability]);

  return (
    <>
      <Space size={15} style={{ marginBottom: 20, minWidth: 1400 }}>
        <Typography.Title level={4} style={{ marginTop: 10, marginLeft: 16 }}>
          Create a new preset:
        </Typography.Title>
        <Button type={'primary'} onClick={() => showCreateModal({})}>
          Create
        </Button>
      </Space>
      <Space size={15} style={{ marginBottom: 35, minWidth: 1400 }}>
        <Typography.Title level={4} style={{ marginTop: 10 }}>
          Select a preset to edit:
        </Typography.Title>
        {/*<Input*/}
        {/*  value={presetName}*/}
        {/*  onChange={e => {*/}
        {/*    setPresetName(e.target.value);*/}
        {/*  }}*/}
        {/*  placeholder="Preset Name"*/}
        {/*/>*/}
        {/*<Button*/}
        {/*  type="primary"*/}
        {/*  disabled={!presetName}*/}
        {/*  onClick={() => {*/}
        {/*    dispatch(createPreset({ name: presetName }));*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <PlusOutlined /> New*/}
        {/*</Button>*/}

        {/*some undo that nobody used or knew what it did*/}
        {/*<Button*/}
        {/*  type="primary"*/}
        {/*  danger*/}
        {/*  disabled={*/}
        {/*    presetId ? presetName === presets[presetId]?.name : !presetName*/}
        {/*  }*/}
        {/*  onClick={() => {*/}
        {/*    setPresetName(presetId ? presets[presetId].name : '');*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <RollbackOutlined />*/}
        {/*</Button>*/}

        <Select
          // noDataBehaviour={noDataBehaviour}
          loading={presetsWaiting}
          value={presetId ?? undefined} // control the value
          showSearch
          allowClear
          style={{ width: 420 }}
          // placeholder="Select Preset"
          placeholder={presetsWaiting ? 'Loading...' : 'Select a preset'}
          filterOption={(input, option) => {
            const optionPreset = presets[Number(option?.value)];
            return (optionPreset?.name || '')
              .toLowerCase()
              .includes(input.toLowerCase());
          }}
          onChange={value => {
            if (value === undefined) {
              // user cleared selection
              setPresetId(undefined);
              setSelected([]);
              setPresetName('');
              return;
            }
            const id = value as number; // don't Number(undefined)
            setPresetId(id);
            setSelected(presets[id]?.assets || []);
            setPresetName(presets[id]?.name || '');
          }}
        >
          {Object.values(presets)
            .sort((a, b) => b.id - a.id)
            .map(({ id, name }) => {
              const availability = trainerAvailability[id];
              return (
                <Select.Option key={id} value={id}>
                  <Space size={6}>
                    <span>{name}</span>
                    {availability && (
                      <Tag
                        color={
                          availability.trainer_available ? 'success' : 'error'
                        }
                      >
                        {availability.trainer_available
                          ? 'Available'
                          : `Unavailable · ${availability.blocking_asset_count}`}
                      </Tag>
                    )}
                  </Space>
                </Select.Option>
              );
            })}
        </Select>
        {/*some other undo button that nobody used or knew what it did*/}
        {/*<Button*/}
        {/*  type="primary"*/}
        {/*  danger*/}
        {/*  disabled={!presetId}*/}
        {/*  onClick={() => {*/}
        {/*    if (presetId) setSelected(presets[presetId].assets);*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <RollbackOutlined />*/}
        {/*</Button>*/}
        <Button
          type="primary"
          disabled={!presetId}
          onClick={() => {
            if (presetId) {
              const prevPresent = presets[presetId];
              dispatch(
                updatePreset({
                  ...prevPresent,
                  name: presetName || prevPresent.name,
                  assets: selected,
                })
              );
            }
          }}
        >
          <SaveOutlined /> Save Assets
        </Button>
        <Button
          type="primary"
          disabled={!presetId}
          onClick={() => {
            if (presetId) {
              showEditNameModal({
                preset_id: presetId,
                preset_name: presetName,
              });
            }
          }}
        >
          Edit Name
        </Button>
        <Popconfirm
          title="Are you sure?"
          onConfirm={() => {
            if (presetId) dispatch(deletePreset({ preset_id: presetId }));
          }}
        >
          <Button danger style={{ marginLeft: 0 }} disabled={!presetId}>
            Delete
          </Button>
        </Popconfirm>
        {selected.length > 0 && (
          <div>
            <Typography.Text type={'secondary'}>
              ({selected.length} instruments selected)
            </Typography.Text>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => {
                if (presetId) {
                  showDuplicatePresetModal({
                    preset_name: presetName,
                    preset_id: presetId,
                  });
                }
              }}
            >
              Duplicate this preset
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => {
                if (presetId) {
                  showMissingPricesModal({
                    preset_name: presetName,
                    preset_id: presetId,
                  });
                }
              }}
            >
              See missing prices
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              onClick={() => {
                if (presetId) {
                  showPresetReturnsModal({
                    preset_name: presetName,
                    preset_id: presetId,
                  });
                }
              }}
            >
              See returns
            </Button>
          </div>
        )}
      </Space>
      {presetId !== undefined && (
        <div style={{ margin: '0 0 20px 0', maxWidth: 1100 }}>
          {selectedAvailability ? (
            <Alert
              showIcon
              type={
                selectedAvailability.trainer_available ? 'success' : 'error'
              }
              title={
                selectedAvailability.trainer_available
                  ? 'Available in Trainer Session presets'
                  : `Unavailable in Trainer Session presets: ${selectedAvailability.blocking_asset_count} assigned instrument${
                      selectedAvailability.blocking_asset_count === 1 ? '' : 's'
                    } not visible`
              }
              description={
                !selectedAvailability.trainer_available ? (
                  <Space
                    orientation="vertical"
                    size={8}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text>
                      Make an instrument visible to remove it from this blocking
                      list. This changes only its Visible setting; it remains in
                      the preset.
                    </Typography.Text>
                    {selectedAvailability.blocking_assets.map(asset => (
                      <Space key={asset.id} wrap>
                        <Typography.Text strong>{asset.name}</Typography.Text>
                        <Typography.Text type="secondary">
                          ID {asset.id} · {asset.ticker}
                        </Typography.Text>
                        <Tag color="error">Not visible</Tag>
                        <Popconfirm
                          title={`Make ${asset.name} visible to trainers?`}
                          description="This instrument may also become available anywhere else trainer-visible instruments are used."
                          okText="Make visible"
                          cancelText="Cancel"
                          onConfirm={() =>
                            dispatch(
                              makePresetAssetVisible({
                                preset_id: presetId,
                                asset_id: asset.id,
                              }),
                            )
                          }
                        >
                          <Button
                            size="small"
                            loading={assetBeingMadeVisible === asset.id}
                          >
                            Make visible
                          </Button>
                        </Popconfirm>
                      </Space>
                    ))}
                    <Space>
                      <Switch
                        size="small"
                        checked={showBlockingOnly}
                        onChange={setShowBlockingOnly}
                      />
                      <Typography.Text>
                        Show blocking instruments only in the table
                      </Typography.Text>
                    </Space>
                  </Space>
                ) : undefined
              }
            />
          ) : (
            <Alert
              showIcon
              type="info"
              title={
                availabilityWaiting
                  ? 'Checking Trainer Session availability…'
                  : 'Trainer Session availability could not be determined'
              }
            />
          )}
        </div>
      )}
      <Table
        loading={!assets.length}
        locale={noDataBehaviour}
        dataSource={displayedAssets}
        rowKey="id"
        pagination={{ defaultPageSize: 20 }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selected,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelected(selectedRowKeys as number[]);
          },
          getCheckboxProps: () => ({
            disabled: !presetId,
          }),
        }}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
          },
          {
            title: 'Name',
            dataIndex: 'name',
            sorter: (a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0),
            ...columnSearchWidget({ dataIndex: 'name' }),
          },
          {
            title: 'BBG Ticker',
            dataIndex: 'ticker',
            sorter: (a, b) =>
              a.ticker < b.ticker ? -1 : a.ticker > b.ticker ? 1 : 0,
            ...columnSearchWidget({ dataIndex: 'ticker' }),
          },
          {
            title: 'Trainer visible',
            dataIndex: 'visible',
            render: value => (
              <Tag color={value ? 'success' : 'error'}>
                {value ? 'Yes' : 'No'}
              </Tag>
            ),
            filters: [
              { text: 'Visible', value: true },
              { text: 'Not visible', value: false },
            ],
            onFilter: (value, record) => value === record.visible,
          },
          {
            title: 'Variable CCY',
            dataIndex: 'variable_currency',
            render: value => currencies[value].symbol,
            filters: Object.values(currencies).map(currency => {
              return {
                text: currency.symbol,
                value: currency.id,
              };
            }),
            onFilter: (value, record) => {
              return value === record.variable_currency;
            },
          },
          {
            title: 'Base CCY',
            dataIndex: 'base_currency',
            render: value => currencies[value].symbol,
            filters: Object.values(currencies).map(currency => {
              return {
                text: currency.symbol,
                value: currency.id,
              };
            }),
            onFilter: (value, record) => {
              return value === record.base_currency;
            },
          },
          {
            title: 'Region',
            dataIndex: 'region',
            render: (value: keyof typeof REGIONS) => REGIONS[value],
            filters: Object.entries(REGIONS).map(([id, region]) => {
              return {
                text: region,
                value: parseInt(id),
              };
            }),
            onFilter: (value, record) => {
              return value === record.region;
            },
          },
          {
            title: 'Geography',
            dataIndex: 'geography',
            filters: Array.from(
              new Set(assets.map(asset => asset.geography))
            ).map(geography => ({ text: geography, value: geography })),
            onFilter: (value, record) => {
              return value === record.geography;
            },
          },
          {
            title: 'Asset Class',
            dataIndex: 'resourcetype',
            filters: Array.from(
              new Set(assets.map(asset => asset.resourcetype))
            ).map(resourcetype => ({
              text: resourcetype,
              value: resourcetype,
            })),
            onFilter: (value, record) => {
              return value === record.resourcetype;
            },
          },
        ]}
      />
      {presetReturnsModal}
      {missingPricesModal}
      {duplicatePresetModal}
      {CreateModal}
      {EditNameModal}
    </>
  );
};

export default PresetsTab;
